/**
 * FoiDeParcursTab - Componenta pentru tab-ul Foi de Parcurs
 * Afișează foi de parcurs organizate pe vehicul cu opțiuni de export și import
 * Include sub-tab pentru Alimentări Importate (reconciliere manuală CSV)
 */
import { memo, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Car, Download, FileText, Fuel, FileSpreadsheet, Check, AlertTriangle, XCircle, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { toast } from 'sonner';
import AlimentariImportTab from './AlimentariImportTab';
import axios from 'axios';
import { API } from '@/App';

// Motive de închidere cursă
const CLOSING_REASONS = {
  'final_day': 'Sfârșit cursă',
  'car_damaged': 'Mașina avariată',
  'engine_problem': 'Problemă motor',
  'other': 'Altele'
};

/**
 * Componenta FoiDeParcursTab
 */
const FoiDeParcursTab = memo(function FoiDeParcursTab({
  cars,
  tripSheets,
  expandedCar,
  selectedMonth,
  selectedYear,
  carSearchQuery,
  setCarSearchQuery,
  handleExpandCar,
  handleMonthChange,
  refreshTripSheets,
  handleTripSheetExport,
  handleAddTrip,
  handleSetFuelLevel,
  handleEditTrip,
  handleDeleteTrip,
  setOpenCentralizatorDialog,
  setOpenCSVUploadDialog,
  fetchImportBatches,
  setShowImportHistory,
  importBatches,
  handleUndoLastImport,
  loading,
  auth,
  fuelAlerts = []
}) {
  // State pentru alimentări CSV indexate
  const [csvFuelData, setCsvFuelData] = useState({});
  const [csvAdBlueData, setCsvAdBlueData] = useState({});

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Preia alimentarea din CSV și o adaugă la cursă
  const copyFuelFromCsv = async (tripId, litri, cost, fuelType = 'motorina') => {
    try {
      await axios.post(`${API}/trips/${tripId}/fuel-entry`, {
        liters: litri,
        cost: cost || 0,
        fuel_type: fuelType,
        note: 'Preluat din CSV'
      }, getAuthHeaders());
      toast.success(`${fuelType === 'adblue' ? 'AdBlue' : 'Alimentare'} preluată din CSV (${litri}L)`);
      if (refreshTripSheets) await refreshTripSheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la preluare din CSV');
    }
  };

  // Funcție pentru a prelua nivelul de combustibil din luna anterioară
  const handleCopyPreviousFuelLevel = async (carId) => {
    try {
      const response = await axios.post(`${API}/monthly-fuel-levels/copy-previous`, {
        car_id: carId,
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear)
      }, getAuthHeaders());
      
      if (response.data.success) {
        toast.success(`Nivel preluat din ${response.data.copied_from}: ${response.data.level}L`);
        if (refreshTripSheets) refreshTripSheets();
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Nu există nivel anterior → deschide dialogul de setare manuală
        toast.info('Nu există nivel anterior. Completați manual.');
        handleSetFuelLevel(carId);
      } else {
        toast.error(error.response?.data?.detail || 'Eroare la preluarea nivelului');
      }
    }
  };

  // Funcție bulk - preia nivelurile din luna anterioară pentru toate mașinile
  const handleCopyPreviousBulk = async () => {
    try {
      const response = await axios.post(`${API}/monthly-fuel-levels/copy-previous-bulk`, {
        month: parseInt(selectedMonth),
        year: parseInt(selectedYear)
      }, getAuthHeaders());
      
      if (response.data.success) {
        toast.success(`Niveluri preluate: ${response.data.updated} mașini actualizate, ${response.data.skipped} omise`);
        if (refreshTripSheets) refreshTripSheets();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la preluarea nivelurilor');
    }
  };

  // Fetch alimentări CSV pentru comparare
  const fetchCsvFuelData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/fuel-imports/by-car-date?year=${selectedYear}&month=${selectedMonth}`,
        getAuthHeaders()
      );
      setCsvFuelData(response.data?.indexed || {});
      setCsvAdBlueData(response.data?.indexed_adblue || {});
    } catch (error) {
      console.error('Error fetching CSV fuel data:', error);
      setCsvFuelData({});
      setCsvAdBlueData({});
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchCsvFuelData();
  }, [fetchCsvFuelData]);

  // Funcție pentru a obține alimentarea CSV pentru o cursă (doar combustibil)
  const getCsvFuelForTrip = (carId, tripDate) => {
    if (!tripDate) return null;
    const dateStr = tripDate.split('T')[0]; // "2026-01-29"
    const key = `${carId}:${dateStr}`;
    return csvFuelData[key] || null;
  };

  // Funcție pentru a obține AdBlue CSV pentru o cursă
  const getCsvAdBlueForTrip = (carId, tripDate) => {
    if (!tripDate) return null;
    const dateStr = tripDate.split('T')[0];
    const key = `${carId}:${dateStr}`;
    return csvAdBlueData[key] || null;
  };

  // Funcție pentru a determina statusul comparației
  const getFuelComparisonStatus = (tripFuel, csvFuel) => {
    if (!tripFuel && !csvFuel) return null; // Nicio alimentare
    if (!tripFuel && csvFuel) return 'fara-cursa'; // CSV există, șofer nu a raportat
    if (tripFuel && !csvFuel) return 'lipsa-csv'; // Șofer a raportat, CSV nu există
    
    // Ambele există - verificăm dacă se potrivesc (±0.5L)
    const diff = Math.abs(tripFuel - csvFuel.litri);
    if (diff <= 0.5) return 'ok';
    return 'diferenta';
  };

  // Funcție pentru a verifica dacă un trip are alimentare suspectă
  const hasSuspiciousFuel = (tripId) => {
    return fuelAlerts.some(alert => alert.trip_id === tripId);
  };

  // Funcție pentru a verifica dacă o mașină are alimentări suspecte
  const carHasSuspiciousFuel = (carId) => {
    return fuelAlerts.some(alert => alert.car_id === carId);
  };

  // Funcție pentru a verifica dacă există km neacoperiți într-o foaie de parcurs
  const hasKmGaps = (carId) => {
    const sheet = tripSheets[carId];
    if (!sheet || !sheet.trips || sheet.trips.length < 2) return false;
    
    for (let i = 1; i < sheet.trips.length; i++) {
      const prevTrip = sheet.trips[i - 1];
      const currTrip = sheet.trips[i];
      if (prevTrip.end_km && currTrip.start_km) {
        const diff = currTrip.start_km - prevTrip.end_km;
        // Alertează dacă: diferența > 1 (km lipsă) SAU diferența < 0 (km au scăzut - eroare!)
        if (diff > 1 || diff < 0) {
          return true;
        }
      }
    }
    return false;
  };

  // Numără câte gap-uri de km există
  const countKmGaps = (carId) => {
    const sheet = tripSheets[carId];
    if (!sheet || !sheet.trips || sheet.trips.length < 2) return 0;
    
    let count = 0;
    for (let i = 1; i < sheet.trips.length; i++) {
      const prevTrip = sheet.trips[i - 1];
      const currTrip = sheet.trips[i];
      if (prevTrip.end_km && currTrip.start_km) {
        const diff = currTrip.start_km - prevTrip.end_km;
        // Numără dacă: diferența > 1 (km lipsă) SAU diferența < 0 (km au scăzut - eroare!)
        if (diff > 1 || diff < 0) {
          count++;
        }
      }
    }
    return count;
  };

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('foi-parcurs');

  // Centralized fuel levels state
  const [fuelLevelsExpanded, setFuelLevelsExpanded] = useState(false);
  const [allFuelLevels, setAllFuelLevels] = useState({});
  const [fuelLevelEdits, setFuelLevelEdits] = useState({}); // {carId: {start_level: '', end_level: ''}}
  const [savingFuelLevel, setSavingFuelLevel] = useState(null); // carId being saved

  // Fetch all fuel levels for the selected month
  const fetchAllFuelLevels = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/monthly-fuel-levels/all?month=${selectedMonth}&year=${selectedYear}`,
        getAuthHeaders()
      );
      setAllFuelLevels(response.data?.fuel_levels || {});
    } catch (error) {
      console.error('Error fetching all fuel levels:', error);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (fuelLevelsExpanded) {
      fetchAllFuelLevels();
    }
  }, [fuelLevelsExpanded, fetchAllFuelLevels]);

  // Initialize edit values when fuel levels load
  useEffect(() => {
    const edits = {};
    cars.forEach(car => {
      const fl = allFuelLevels[car.id];
      edits[car.id] = {
        start_level: fl?.start_level ?? '',
        end_level: fl?.end_level ?? ''
      };
    });
    setFuelLevelEdits(edits);
  }, [allFuelLevels, cars]);

  // Save fuel level for a specific car
  const saveFuelLevelForCar = async (carId) => {
    const edit = fuelLevelEdits[carId];
    if (!edit) return;
    setSavingFuelLevel(carId);
    try {
      // Sequential calls to avoid duplicate key errors
      if (edit.start_level !== '' && edit.start_level !== null) {
        await axios.post(`${API}/monthly-fuel-levels`, {
          car_id: carId, month: selectedMonth, year: selectedYear,
          level: parseFloat(edit.start_level), level_type: 'start'
        }, getAuthHeaders());
      }
      if (edit.end_level !== '' && edit.end_level !== null) {
        await axios.post(`${API}/monthly-fuel-levels`, {
          car_id: carId, month: selectedMonth, year: selectedYear,
          level: parseFloat(edit.end_level), level_type: 'end'
        }, getAuthHeaders());
      }
      toast.success(`Nivel rezervor salvat pentru ${cars.find(c => c.id === carId)?.license_plate || carId}`);
      fetchAllFuelLevels();
    } catch (error) {
      toast.error('Eroare la salvarea nivelului');
    } finally {
      setSavingFuelLevel(null);
    }
  };

  // Helper: get month name
  const getMonthName = () => new Date(selectedYear, selectedMonth - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4">
      {/* Sub-tabs navigation */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveSubTab('foi-parcurs')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors flex items-center gap-2 ${
            activeSubTab === 'foi-parcurs'
              ? 'bg-blue-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <FileSpreadsheet className="w-4 h-4" />
          Foi de Parcurs
        </button>
        <button
          onClick={() => setActiveSubTab('alimentari-import')}
          className={`px-4 py-2 rounded-t-lg font-medium text-sm transition-colors flex items-center gap-2 ${
            activeSubTab === 'alimentari-import'
              ? 'bg-green-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Fuel className="w-4 h-4" />
          Alimentări
        </button>
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'alimentari-import' ? (
        <AlimentariImportTab />
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>Foi de parcurs</CardTitle>
          <CardDescription>Foi de parcurs organizate pe vehicul</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Centralizator Export Button */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                  📊 Centralizator Lunar Flotă
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Export complet pentru toate mașinile: kilometraj, combustibil, costuri
                </p>
              </div>
              <button
                onClick={() => setOpenCentralizatorDialog(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-md"
              >
                📊 Export Centralizator
              </button>
            </div>
          </div>

          {/* Centralizat Nivel Rezervor */}
          <div className="mb-4">
            <button
              onClick={() => setFuelLevelsExpanded(!fuelLevelsExpanded)}
              className="w-full flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              data-testid="fuel-levels-toggle"
            >
              <div className="flex items-center gap-2">
                <Fuel className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-900">Nivel Rezervor - Toate Mașinile</span>
                <span className="text-sm text-amber-600 ml-2">
                  ({getMonthName()})
                </span>
              </div>
              {fuelLevelsExpanded
                ? <ChevronUp className="w-5 h-5 text-amber-600" />
                : <ChevronDown className="w-5 h-5 text-amber-600" />
              }
            </button>

            {fuelLevelsExpanded && (
              <div className="mt-2 border border-amber-200 rounded-lg overflow-hidden" data-testid="fuel-levels-table">
                {/* Buton bulk: Preia din luna anterioară pentru TOATE mașinile */}
                <div className="p-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between">
                  <p className="text-sm text-amber-800">
                    Completează automat nivelurile din luna anterioară pentru mașinile fără date.
                  </p>
                  <button
                    onClick={handleCopyPreviousBulk}
                    data-testid="copy-prev-fuel-bulk"
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Fuel className="w-4 h-4" />
                    Preia Toate din Luna Anterioară
                  </button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-amber-50 text-xs">
                      <TableHead className="py-2 px-3 font-semibold w-[180px]">Mașină</TableHead>
                      <TableHead className="py-2 px-3 font-semibold text-center w-[120px]">Tip Combustibil</TableHead>
                      <TableHead className="py-2 px-3 font-semibold text-center w-[160px]">Început Lună (L)</TableHead>
                      <TableHead className="py-2 px-3 font-semibold text-center w-[160px]">Sfârșit Lună (L)</TableHead>
                      <TableHead className="py-2 px-3 font-semibold text-center w-[100px]">Diferență</TableHead>
                      <TableHead className="py-2 px-3 font-semibold text-center w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cars
                      .filter(car => {
                        const name = (car.name || '').toLowerCase();
                        const plate = (car.license_plate || '').toLowerCase();
                        return !name.includes('test') && !plate.includes('test');
                      })
                      .map((car) => {
                        const edit = fuelLevelEdits[car.id] || { start_level: '', end_level: '' };
                        const startVal = parseFloat(edit.start_level);
                        const endVal = parseFloat(edit.end_level);
                        const hasStart = edit.start_level !== '' && !isNaN(startVal);
                        const hasEnd = edit.end_level !== '' && !isNaN(endVal);
                        const diff = (hasStart && hasEnd) ? (startVal - endVal) : null;
                        const isSaving = savingFuelLevel === car.id;
                        const orig = allFuelLevels[car.id];
                        const hasChanges = (
                          String(edit.start_level) !== String(orig?.start_level ?? '') ||
                          String(edit.end_level) !== String(orig?.end_level ?? '')
                        );

                        return (
                          <TableRow key={car.id} className="text-sm hover:bg-amber-50/40">
                            <TableCell className="py-1.5 px-3">
                              <div className="font-semibold text-sm leading-tight">{car.license_plate}</div>
                              <div className="text-[11px] text-slate-500 leading-tight">{car.name}</div>
                            </TableCell>
                            <TableCell className="py-1.5 px-3 text-center">
                              {(() => {
                                const ft = (car.fuel_type || 'Motorină').toLowerCase().trim();
                                const isBenzina = ft.includes('benzin');
                                const label = isBenzina ? 'Benzină' : 'Motorină';
                                return (
                                  <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                                    isBenzina
                                      ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                                      : 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                  }`}>
                                    {label}
                                  </span>
                                );
                              })()}
                            </TableCell>
                            <TableCell className="py-1.5 px-3">
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                placeholder="0"
                                value={edit.start_level}
                                onChange={(e) => setFuelLevelEdits(prev => ({
                                  ...prev,
                                  [car.id]: { ...prev[car.id], start_level: e.target.value }
                                }))}
                                onKeyDown={(e) => e.key === 'Enter' && saveFuelLevelForCar(car.id)}
                                className="h-8 text-center text-sm w-full"
                                data-testid={`fuel-start-${car.id}`}
                              />
                            </TableCell>
                            <TableCell className="py-1.5 px-3">
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                placeholder="0"
                                value={edit.end_level}
                                onChange={(e) => setFuelLevelEdits(prev => ({
                                  ...prev,
                                  [car.id]: { ...prev[car.id], end_level: e.target.value }
                                }))}
                                onKeyDown={(e) => e.key === 'Enter' && saveFuelLevelForCar(car.id)}
                                className="h-8 text-center text-sm w-full"
                                data-testid={`fuel-end-${car.id}`}
                              />
                            </TableCell>
                            <TableCell className="py-1.5 px-3 text-center">
                              {diff !== null ? (
                                <span className={`font-mono font-semibold text-sm ${diff > 0 ? 'text-red-600' : diff < 0 ? 'text-green-600' : 'text-slate-500'}`}>
                                  {diff > 0 ? `-${diff.toFixed(1)}L` : diff < 0 ? `+${Math.abs(diff).toFixed(1)}L` : '0L'}
                                </span>
                              ) : (
                                <span className="text-slate-300 text-xs">—</span>
                              )}
                            </TableCell>
                            <TableCell className="py-1.5 px-3 text-center">
                              {hasChanges && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => saveFuelLevelForCar(car.id)}
                                  disabled={isSaving}
                                  className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`fuel-save-${car.id}`}
                                >
                                  {isSaving ? <span className="animate-spin text-xs">⏳</span> : <Save className="w-4 h-4" />}
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {/* Totaluri separate pe tip combustibil */}
                    {(() => {
                      const filteredCars = cars.filter(car => {
                        const name = (car.name || '').toLowerCase();
                        const plate = (car.license_plate || '').toLowerCase();
                        return !name.includes('test') && !plate.includes('test');
                      });

                      // Normalizare tip combustibil (Motorina/Motorină -> Motorină, Benzina/Benzină -> Benzină)
                      const normalizeFuelType = (ft) => {
                        const lower = (ft || '').toLowerCase().trim();
                        if (lower.includes('benzin')) return 'Benzină';
                        return 'Motorină'; // default
                      };

                      // Calculează totaluri pe tip combustibil
                      const totals = {};
                      filteredCars.forEach(car => {
                        const fuelType = normalizeFuelType(car.fuel_type || 'Motorină');
                        if (!totals[fuelType]) {
                          totals[fuelType] = { start: 0, end: 0, countStart: 0, countEnd: 0, countCars: 0 };
                        }
                        totals[fuelType].countCars++;
                        const edit = fuelLevelEdits[car.id] || {};
                        const s = parseFloat(edit.start_level);
                        const e = parseFloat(edit.end_level);
                        if (!isNaN(s) && edit.start_level !== '') { totals[fuelType].start += s; totals[fuelType].countStart++; }
                        if (!isNaN(e) && edit.end_level !== '') { totals[fuelType].end += e; totals[fuelType].countEnd++; }
                      });

                      // Ordinea fixă: Motorină prima, apoi Benzină, apoi restul
                      const sortedTypes = Object.keys(totals).sort((a, b) => {
                        if (a.toLowerCase().includes('motorin')) return -1;
                        if (b.toLowerCase().includes('motorin')) return 1;
                        if (a.toLowerCase().includes('benzin')) return -1;
                        if (b.toLowerCase().includes('benzin')) return 1;
                        return a.localeCompare(b);
                      });

                      // Calculează totalul general
                      let grandStart = 0, grandEnd = 0, grandCountStart = 0, grandCountEnd = 0;
                      Object.values(totals).forEach(t => {
                        grandStart += t.start; grandEnd += t.end;
                        grandCountStart += t.countStart; grandCountEnd += t.countEnd;
                      });
                      const grandDiff = (grandCountStart > 0 && grandCountEnd > 0) ? (grandStart - grandEnd) : null;

                      return (
                        <>
                          {sortedTypes.map(fuelType => {
                            const t = totals[fuelType];
                            const diff = (t.countStart > 0 && t.countEnd > 0) ? (t.start - t.end) : null;
                            const isMotorina = fuelType.toLowerCase().includes('motorin');
                            const bgClass = isMotorina ? 'bg-yellow-50 border-yellow-300' : 'bg-emerald-50 border-emerald-300';
                            const textClass = isMotorina ? 'text-yellow-800' : 'text-emerald-800';
                            return (
                              <TableRow key={fuelType} className={`${bgClass} border-t-2 font-semibold`}>
                                <TableCell className={`py-2 px-3 text-sm ${textClass}`}>
                                  Total {fuelType}
                                </TableCell>
                                <TableCell className={`py-2 px-3 text-center text-[11px] ${textClass}`}>
                                  {t.countCars} mașini
                                </TableCell>
                                <TableCell className={`py-2 px-3 text-center text-sm font-mono ${textClass}`} data-testid={`fuel-total-start-${fuelType.toLowerCase().replace(/[ăîâșț]/g, c => ({ă:'a',î:'i',â:'a',ș:'s',ț:'t'}[c]))}`}>
                                  {t.countStart > 0 ? `${t.start.toFixed(2)} L` : '—'}
                                </TableCell>
                                <TableCell className={`py-2 px-3 text-center text-sm font-mono ${textClass}`} data-testid={`fuel-total-end-${fuelType.toLowerCase().replace(/[ăîâșț]/g, c => ({ă:'a',î:'i',â:'a',ș:'s',ț:'t'}[c]))}`}>
                                  {t.countEnd > 0 ? `${t.end.toFixed(2)} L` : '—'}
                                </TableCell>
                                <TableCell className="py-2 px-3 text-center">
                                  {diff !== null ? (
                                    <span className={`font-mono font-bold text-sm ${diff > 0 ? 'text-red-700' : diff < 0 ? 'text-green-700' : 'text-slate-600'}`}>
                                      {diff > 0 ? `-${diff.toFixed(2)}L` : diff < 0 ? `+${Math.abs(diff).toFixed(2)}L` : '0L'}
                                    </span>
                                  ) : <span className="text-slate-400">—</span>}
                                </TableCell>
                                <TableCell className="py-2 px-3"></TableCell>
                              </TableRow>
                            );
                          })}
                          {/* Rând TOTAL GENERAL */}
                          <TableRow className="bg-amber-100/70 border-t-2 border-amber-400 font-bold">
                            <TableCell className="py-2 px-3 text-sm text-amber-900">
                              TOTAL GENERAL
                            </TableCell>
                            <TableCell className="py-2 px-3 text-center text-[11px] text-amber-900">
                              {filteredCars.length} mașini
                            </TableCell>
                            <TableCell className="py-2 px-3 text-center text-sm font-mono text-amber-900" data-testid="fuel-total-start">
                              {grandCountStart > 0 ? `${grandStart.toFixed(2)} L` : '—'}
                            </TableCell>
                            <TableCell className="py-2 px-3 text-center text-sm font-mono text-amber-900" data-testid="fuel-total-end">
                              {grandCountEnd > 0 ? `${grandEnd.toFixed(2)} L` : '—'}
                            </TableCell>
                            <TableCell className="py-2 px-3 text-center" data-testid="fuel-total-diff">
                              {grandDiff !== null ? (
                                <span className={`font-mono font-bold text-sm ${grandDiff > 0 ? 'text-red-700' : grandDiff < 0 ? 'text-green-700' : 'text-slate-600'}`}>
                                  {grandDiff > 0 ? `-${grandDiff.toFixed(2)}L` : grandDiff < 0 ? `+${Math.abs(grandDiff).toFixed(2)}L` : '0L'}
                                </span>
                              ) : <span className="text-slate-400">—</span>}
                            </TableCell>
                            <TableCell className="py-2 px-3"></TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Search Box */}
          <div className="mb-4">
            <Input
              placeholder="🔍 Caută mașină (nume sau număr înmatriculare)..."
              value={carSearchQuery}
              onChange={(e) => setCarSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>

          {/* Cars List */}
          {cars.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Car className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">Nu există vehicule</p>
              <p className="text-sm mt-2">Adaugă vehicule pentru a începe urmărirea curselor</p>
            </div>
          ) : (
            cars
              .filter(car => 
                carSearchQuery === '' || 
                car.name.toLowerCase().includes(carSearchQuery.toLowerCase()) ||
                car.license_plate.toLowerCase().includes(carSearchQuery.toLowerCase())
              )
              .map((car) => {
                const kmGapCount = countKmGaps(car.id);
                const hasGaps = kmGapCount > 0;
                
                return (
                <Card key={car.id} className={`border mb-1.5 ${hasGaps ? 'border-red-300 bg-red-50/30' : ''}`}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-slate-50 transition-colors py-2 px-3"
                    data-testid={`car-header-${car.id}`}
                    onClick={() => handleExpandCar(car.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Car className={`w-4 h-4 ${hasGaps ? 'text-red-500' : 'text-blue-600'}`} />
                        <div>
                          <CardTitle className="text-sm font-semibold leading-tight">{car.license_plate}</CardTitle>
                          <CardDescription className="text-[11px] leading-tight">{car.name}</CardDescription>
                        </div>
                        {/* Indicator km neacoperiți */}
                        {hasGaps && (
                          <span 
                            className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 flex items-center gap-1"
                            title={`${kmGapCount} ${kmGapCount === 1 ? 'cursă cu' : 'curse cu'} km neacoperiți (diferență > 1 km)`}
                          >
                            ⚠️ {kmGapCount} km gap{kmGapCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          car.status === 'available'
                            ? 'bg-green-50 text-green-600 border border-green-200'
                            : car.status === 'in_use'
                            ? 'bg-amber-50 text-amber-600 border border-amber-200'
                            : car.status === 'damaged'
                            ? 'bg-orange-50 text-orange-600 border border-orange-200'
                            : car.status === 'engine_problem'
                            ? 'bg-rose-50 text-rose-600 border border-rose-200'
                            : 'bg-slate-50 text-slate-600 border border-slate-200'
                        }`}>
                          {car.status === 'available' ? 'Disponibil' 
                            : car.status === 'in_use' ? 'În uz'
                            : car.status === 'damaged' ? 'Avariat'
                            : car.status === 'engine_problem' ? 'Problemă motor'
                            : car.status === 'nonavailable' ? 'Indisponibil'
                            : car.status}
                        </span>
                        <span className="text-slate-400 text-xs">
                          {expandedCar === car.id ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedCar === car.id && (
                    <CardContent className="space-y-3 pt-3 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label>Perioadă:</Label>
                          <Select 
                            value={`${selectedMonth}-${selectedYear}`}
                            onValueChange={(value) => {
                              const [month, year] = value.split('-').map(Number);
                              handleMonthChange(month, year);
                            }}
                          >
                            <SelectTrigger className="w-56" data-testid="trip-period-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                              {/* Generate months for current year and previous 2 years */}
                              {(() => {
                                const currentYear = new Date().getFullYear();
                                const options = [];
                                for (let year = currentYear; year >= currentYear - 2; year--) {
                                  for (let month = 12; month >= 1; month--) {
                                    const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long' });
                                    options.push(
                                      <SelectItem key={`${month}-${year}`} value={`${month}-${year}`}>
                                        {monthName.charAt(0).toUpperCase() + monthName.slice(1)} {year}
                                      </SelectItem>
                                    );
                                  }
                                }
                                return options;
                              })()}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleTripSheetExport(car.id, 'pdf')} disabled={loading}>
                            <Download className="w-4 h-4 mr-2" />PDF
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleTripSheetExport(car.id, 'excel')} disabled={loading}>
                            <Download className="w-4 h-4 mr-2" />Excel
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleTripSheetExport(car.id, 'csv')} disabled={loading}>
                            <Download className="w-4 h-4 mr-2" />CSV
                          </Button>
                        </div>
                      </div>
                      
                      {tripSheets[car.id] ? (
                        <>
                          {tripSheets[car.id].fuel_level && (
                            <div className="bg-blue-50 p-3 rounded-lg text-sm">
                              <p className="font-medium text-blue-900">Nivel combustibil:</p>
                              <div className="flex gap-4 mt-1 text-blue-700">
                                {tripSheets[car.id].fuel_level.start_level && (
                                  <span>Început lună: {tripSheets[car.id].fuel_level.start_level}L</span>
                                )}
                                {tripSheets[car.id].fuel_level.end_level && (
                                  <span>Final lună: {tripSheets[car.id].fuel_level.end_level}L</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="mb-4 flex gap-2 flex-wrap">
                            <button
                              onClick={() => handleAddTrip(car.id)}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                            >
                              <span>+ Adaugă Cursă Nouă</span>
                            </button>
                            <button
                              onClick={() => handleSetFuelLevel(car.id)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                            >
                              <span>⛽ Setează Nivel Rezervor</span>
                            </button>
                            <button
                              onClick={() => handleCopyPreviousFuelLevel(car.id)}
                              data-testid={`copy-prev-fuel-${car.id}`}
                              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                              title="Preia nivelul de sfârșit de lună anterioară ca nivel de început și sfârșit pentru luna curentă"
                            >
                              <span>⛽ Preia Luna Anterioară</span>
                            </button>
                          </div>
                          
                          {tripSheets[car.id].trips && tripSheets[car.id].trips.length > 0 ? (
                            <>
                              <Table>
                                <TableHeader>
                                  <TableRow className="text-xs">
                                    <TableHead className="py-2 px-2">Data</TableHead>
                                    <TableHead className="py-2 px-2">Șofer</TableHead>
                                    <TableHead className="py-2 px-2">Traseu</TableHead>
                                    <TableHead className="py-2 px-2">Scop deplasare</TableHead>
                                    <TableHead className="py-2 px-2">Km Start</TableHead>
                                    <TableHead className="py-2 px-2">Km End</TableHead>
                                    <TableHead className="py-2 px-2">Dist.</TableHead>
                                    <TableHead className="py-2 px-2">Motiv</TableHead>
                                    <TableHead className="py-2 px-2">Închis de</TableHead>
                                    <TableHead className="py-2 px-2">Alim. Șofer</TableHead>
                                    <TableHead className="py-2 px-2">AdBlue Șofer</TableHead>
                                    <TableHead className="py-2 px-2">Comb. CSV</TableHead>
                                    <TableHead className="py-2 px-2">AdBlue CSV</TableHead>
                                    <TableHead className="py-2 px-2">Consum</TableHead>
                                    <TableHead className="py-2 px-2">Status</TableHead>
                                    <TableHead className="py-2 px-2">Note</TableHead>
                                    <TableHead className="py-2 px-2"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {tripSheets[car.id].trips.map((trip, tripIndex) => {
                                    // Verifică km neacoperiți: compară start_km cu end_km al cursei anterioare
                                    const prevTrip = tripIndex > 0 ? tripSheets[car.id].trips[tripIndex - 1] : null;
                                    const kmDiff = prevTrip && prevTrip.end_km && trip.start_km ? (trip.start_km - prevTrip.end_km) : 0;
                                    // Alertează dacă: diferența > 1 (km lipsă) SAU diferența < 0 (km au scăzut!)
                                    const hasKmGap = prevTrip && prevTrip.end_km && trip.start_km && (kmDiff > 1 || kmDiff < 0);
                                    const isKmDecreased = kmDiff < 0; // Km au scăzut - eroare gravă!
                                    
                                    // Obținem alimentarea CSV pentru această cursă
                                    const csvFuel = getCsvFuelForTrip(car.id, trip.start_time);
                                    const fuelStatus = getFuelComparisonStatus(trip.fuel_liters, csvFuel);
                                    
                                    // Calcul consum între alimentări (din plin în plin)
                                    const consumCalc = (() => {
                                      const entries = trip.fuel_entries || [];
                                      const motorina = entries.filter(e => !e.fuel_type || e.fuel_type === 'motorina');
                                      const currentLiters = motorina.reduce((sum, e) => sum + (e.liters || 0), 0);
                                      if (!currentLiters) return null;
                                      
                                      // Fallback: cursă fără fuel_entries dar cu fuel_liters
                                      const effectiveLiters = currentLiters || (trip.fuel_liters > 0 ? trip.fuel_liters : 0);
                                      if (!effectiveLiters) return null;
                                      
                                      // KM la alimentarea curentă: km din entry > end_km > start_km
                                      const currentKm = motorina.find(e => e.km)?.km || trip.end_km || trip.start_km;
                                      if (!currentKm) return null;
                                      
                                      // Caută alimentarea anterioară (cursa anterioară cu motorină)
                                      let found = false;
                                      for (let i = tripIndex - 1; i >= 0; i--) {
                                        const prevTrip = tripSheets[car.id].trips[i];
                                        const prevEntries = (prevTrip.fuel_entries || [])
                                          .filter(e => !e.fuel_type || e.fuel_type === 'motorina');
                                        const prevLiters = prevEntries.reduce((sum, e) => sum + (e.liters || 0), 0);
                                        const hasFuel = prevLiters > 0 || (prevTrip.fuel_liters > 0 && prevEntries.length === 0);
                                        if (!hasFuel) continue;
                                        
                                        const prevKm = prevEntries.find(e => e.km)?.km || prevTrip.end_km || prevTrip.start_km;
                                        if (prevKm && prevKm < currentKm) {
                                          const kmDriven = currentKm - prevKm;
                                          const consum = (effectiveLiters / kmDriven) * 100;
                                          return { consum: consum.toFixed(1), kmDriven, liters: effectiveLiters, prevKm, currentKm };
                                        }
                                        found = true;
                                        break;
                                      }
                                      
                                      // Prima alimentare din lună → folosim rest_rezervor și km_start al primei curse
                                      if (!found) {
                                        const firstTrip = tripSheets[car.id].trips[0];
                                        const firstKm = firstTrip?.start_km;
                                        const startLevel = parseFloat(tripSheets[car.id].fuel_level?.start_level) || 0;
                                        const tankCapacity = car.fuel_capacity || 0;
                                        
                                        if (firstKm && firstKm < currentKm) {
                                          const kmDriven = currentKm - firstKm;
                                          
                                          if (startLevel > 0 && tankCapacity > 0) {
                                            // Consum = rest_rezervor - (capacitate - litri_alimentați)
                                            const remainingBeforeFill = tankCapacity - effectiveLiters;
                                            const consumed = startLevel - remainingBeforeFill;
                                            if (consumed > 0) {
                                              const consum = (consumed / kmDriven) * 100;
                                              return { consum: consum.toFixed(1), kmDriven, liters: consumed, prevKm: firstKm, currentKm, isFirst: true, startLevel, tankCapacity };
                                            }
                                          }
                                          // Fallback fără capacitate: folosim litrii alimentați
                                          const consum = (effectiveLiters / kmDriven) * 100;
                                          return { consum: consum.toFixed(1), kmDriven, liters: effectiveLiters, prevKm: firstKm, currentKm, isFirst: true };
                                        }
                                      }
                                      return null;
                                    })();
                                    const consumNormat = car.consum_normat;
                                    
                                    return (
                                    <TableRow key={trip.id} className="text-xs">
                                      <TableCell className="py-1.5 px-2 font-medium">
                                        <div className="leading-tight">
                                          {new Date(trip.start_time).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })}
                                        </div>
                                        <div className="text-[10px] text-slate-500 leading-tight">
                                          {new Date(trip.start_time).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                      </TableCell>
                                      <TableCell className="py-1.5 px-2">{trip.driver_name}</TableCell>
                                      <TableCell className="py-1.5 px-2 max-w-[150px] truncate">
                                        <span className="font-semibold text-blue-600">{trip.route || trip.departure_location}</span>
                                      </TableCell>
                                      <TableCell className="py-1.5 px-2 max-w-[150px] truncate text-slate-600">
                                        {trip.scop_deplasare || '-'}
                                      </TableCell>
                                      <TableCell 
                                        className={`py-1.5 px-2 ${hasKmGap ? (isKmDecreased ? 'text-red-600 font-bold bg-red-50' : 'text-orange-600 font-bold') : ''}`} 
                                        title={hasKmGap ? (isKmDecreased ? `🚨 EROARE! Km au SCĂZUT cu ${Math.abs(kmDiff)} km față de cursa anterioară (${prevTrip.end_km} → ${trip.start_km})` : `⚠️ Km neacoperiți! Diferență: +${kmDiff} km față de cursa anterioară`) : ''}
                                      >
                                        {trip.start_km}
                                        {hasKmGap && <span className="text-[9px] ml-1">{isKmDecreased ? '🚨' : '⚠️'}</span>}
                                      </TableCell>
                                      <TableCell className="py-1.5 px-2">{trip.end_km || '-'}</TableCell>
                                      <TableCell className="py-1.5 px-2">{trip.distance ? `${trip.distance}km` : '-'}</TableCell>
                                      <TableCell className="py-1.5 px-2">
                                        {trip.closing_reason ? (
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            trip.closing_reason === 'final_day' 
                                              ? 'bg-green-50 text-green-600 border border-green-200'
                                              : trip.closing_reason === 'car_damaged'
                                              ? 'bg-orange-50 text-orange-600 border border-orange-200'
                                              : trip.closing_reason === 'engine_problem'
                                              ? 'bg-rose-50 text-rose-600 border border-rose-200'
                                              : 'bg-blue-50 text-blue-600 border border-blue-200'
                                          }`}>
                                            {CLOSING_REASONS[trip.closing_reason] || trip.closing_reason}
                                          </span>
                                        ) : '-'}
                                      </TableCell>
                                      <TableCell className="py-1.5 px-2">
                                        {trip.closed_by ? (
                                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600 border border-purple-200">
                                            {trip.closed_by}
                                          </span>
                                        ) : (
                                          <span className="text-[10px] text-slate-400">șofer</span>
                                        )}
                                      </TableCell>
                                      {/* Alim. Șofer (doar motorină) */}
                                      <TableCell className="py-1.5 px-2">
                                        {(() => {
                                          const entries = trip.fuel_entries || [];
                                          const motorina = entries.filter(e => !e.fuel_type || e.fuel_type === 'motorina');
                                          const totalLiters = motorina.length > 0 
                                            ? motorina.reduce((sum, e) => sum + (e.liters || 0), 0)
                                            : (entries.length === 0 ? trip.fuel_liters : 0);
                                          const totalCost = motorina.length > 0
                                            ? motorina.reduce((sum, e) => sum + (e.cost || 0), 0)
                                            : (entries.length === 0 ? trip.fuel_cost : 0);
                                          const fuelKm = motorina.length > 0
                                            ? motorina.find(e => e.km)?.km
                                            : null;
                                          return (
                                            <div className="text-[11px]">
                                              {totalLiters ? (
                                                <>
                                                  <div className="font-semibold leading-tight flex items-center gap-1">
                                                    {totalLiters.toFixed ? totalLiters.toFixed(2) : totalLiters}L
                                                    {hasSuspiciousFuel(trip.id) && (
                                                      <span className="inline-block" title="Alimentare suspectă">🔴</span>
                                                    )}
                                                  </div>
                                                  {totalCost > 0 && <div className="text-[10px] text-slate-500 leading-tight">{totalCost.toFixed ? totalCost.toFixed(2) : totalCost} RON</div>}
                                                  {fuelKm && <div className="text-[10px] text-indigo-500 leading-tight">@ {fuelKm.toLocaleString()} km</div>}
                                                </>
                                              ) : <span className="text-slate-400">-</span>}
                                              {csvFuel && (
                                                <button
                                                  onClick={() => copyFuelFromCsv(trip.id, csvFuel.litri, csvFuel.cost, 'motorina')}
                                                  className="mt-1 text-[9px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 hover:bg-cyan-200 font-medium whitespace-nowrap"
                                                  title={`Preia ${csvFuel.litri?.toFixed(2)}L / ${csvFuel.cost?.toFixed(2)} RON din CSV`}
                                                >
                                                  ← CSV
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </TableCell>
                                      {/* AdBlue Șofer */}
                                      <TableCell className="py-1.5 px-2">
                                        {(() => {
                                          const entries = trip.fuel_entries || [];
                                          const adblue = entries.filter(e => e.fuel_type === 'adblue');
                                          const csvAdBlue = getCsvAdBlueForTrip(car.id, trip.start_time);
                                          const totalLiters = adblue.reduce((sum, e) => sum + (e.liters || 0), 0);
                                          const totalCost = adblue.reduce((sum, e) => sum + (e.cost || 0), 0);
                                          return (
                                            <div className="text-[11px]">
                                              {totalLiters > 0 ? (
                                                <>
                                                  <div className="font-semibold leading-tight text-sky-600">
                                                    {totalLiters.toFixed(2)}L
                                                  </div>
                                                  {totalCost > 0 && <div className="text-[10px] text-slate-500 leading-tight">{totalCost.toFixed(2)} RON</div>}
                                                </>
                                              ) : <span className="text-slate-400">-</span>}
                                              {csvAdBlue && (
                                                <button
                                                  onClick={() => copyFuelFromCsv(trip.id, csvAdBlue.litri, csvAdBlue.cost, 'adblue')}
                                                  className="mt-1 text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium whitespace-nowrap"
                                                  title={`Preia AdBlue ${csvAdBlue.litri?.toFixed(2)}L / ${csvAdBlue.cost?.toFixed(2)} RON din CSV`}
                                                >
                                                  ← CSV
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </TableCell>
                                      {/* Comb. CSV */}
                                      <TableCell className="py-1.5 px-2">
                                        {csvFuel ? (
                                          <div className="text-[11px]">
                                            <div className="font-semibold leading-tight text-cyan-700">
                                              {csvFuel.litri?.toFixed(2)}L
                                            </div>
                                            <div className="text-[10px] text-slate-500 leading-tight">{csvFuel.cost?.toFixed(2)} RON</div>
                                            {csvFuel.entries?.length > 0 && (
                                              <div className="text-[9px] text-slate-400" title={csvFuel.entries.map(e => `${e.ora} - ${e.statie}`).join('\n')}>
                                                {csvFuel.entries.length} înreg.
                                              </div>
                                            )}
                                          </div>
                                        ) : '-'}
                                      </TableCell>
                                      {/* AdBlue CSV */}
                                      <TableCell className="py-1.5 px-2">
                                        {(() => {
                                          const csvAdBlue = getCsvAdBlueForTrip(car.id, trip.start_time);
                                          return csvAdBlue ? (
                                            <div className="text-[11px]">
                                              <div className="font-semibold leading-tight text-blue-600">
                                                {csvAdBlue.litri?.toFixed(2)}L
                                              </div>
                                              <div className="text-[10px] text-slate-500 leading-tight">{csvAdBlue.cost?.toFixed(2)} RON</div>
                                            </div>
                                          ) : '-';
                                        })()}
                                      </TableCell>
                                      {/* Consum L/100km */}
                                      <TableCell className="py-1.5 px-2">
                                        {consumCalc ? (
                                          <div className="text-[11px]" title={`${consumCalc.liters}L / ${consumCalc.kmDriven}km (km ${consumCalc.prevKm.toLocaleString()} → ${consumCalc.currentKm.toLocaleString()})`}>
                                            <div className={`font-bold leading-tight ${
                                              consumNormat && parseFloat(consumCalc.consum) > consumNormat
                                                ? 'text-red-600'
                                                : 'text-green-600'
                                            }`}>
                                              {consumCalc.consum} L/100
                                            </div>
                                            {consumNormat && (
                                              <div className="text-[9px] text-slate-400">normat: {consumNormat}</div>
                                            )}
                                          </div>
                                        ) : <span className="text-slate-300 text-[10px]">-</span>}
                                      </TableCell>
                                      {/* Status Comparare */}
                                      <TableCell className="py-1.5 px-2">
                                        {fuelStatus === 'ok' && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
                                            <Check className="w-3 h-3" /> OK
                                          </span>
                                        )}
                                        {fuelStatus === 'diferenta' && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-700" title={`Diferență: ${Math.abs(trip.fuel_liters - csvFuel.litri).toFixed(2)}L`}>
                                            <AlertTriangle className="w-3 h-3" /> Dif.
                                          </span>
                                        )}
                                        {fuelStatus === 'lipsa-csv' && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700" title="Șoferul a raportat alimentare dar nu există în CSV">
                                            <XCircle className="w-3 h-3" /> Lipsă
                                          </span>
                                        )}
                                        {fuelStatus === 'fara-cursa' && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700" title="Există în CSV dar șoferul nu a raportat">
                                            <AlertTriangle className="w-3 h-3" /> ?
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell className="py-1.5 px-2 text-[11px] max-w-[100px] truncate">{trip.notes || '-'}</TableCell>
                                      <TableCell className="py-1.5 px-2">
                                        <div className="flex gap-1 text-[10px]">
                                          <button data-testid={`edit-trip-${trip.id}`} onClick={() => handleEditTrip(trip)} className="text-blue-600 hover:text-blue-800 hover:underline">
                                            Edit
                                          </button>
                                          {auth?.user?.role === 'admin' && (
                                            <>
                                              <span className="text-slate-300">|</span>
                                              <button onClick={() => handleDeleteTrip(trip.id)} className="text-red-600 hover:text-red-800 hover:underline">
                                                Delete
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              
                              {/* Summary */}
                              <div className="bg-slate-50 p-2.5 rounded-lg">
                                <p className="font-medium text-slate-900 mb-1.5 text-xs">
                                  📊 Rezumat {new Date(selectedYear, selectedMonth - 1).toLocaleString('ro-RO', { month: 'long' })}:
                                </p>
                                <div className="grid grid-cols-7 gap-3 text-xs">
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Total curse</p>
                                    <p className="text-sm font-semibold">{tripSheets[car.id].summary.total_trips}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Total km</p>
                                    <p className="text-sm font-semibold">{tripSheets[car.id].summary.total_distance} km</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Comb. Alimentat</p>
                                    <p className="text-sm font-semibold">{tripSheets[car.id].summary.total_fuel_liters}L</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Cost comb.</p>
                                    <p className="text-sm font-semibold">{tripSheets[car.id].summary.total_fuel_cost} RON</p>
                                  </div>
                                  {/* AdBlue Total din CSV */}
                                  <div>
                                    <p className="text-slate-600 text-[10px]">AdBlue (CSV)</p>
                                    <p className="text-sm font-semibold text-blue-600">
                                      {(() => {
                                        // Calculăm totalul AdBlue pentru această mașină din csvAdBlueData
                                        let totalAdBlue = 0;
                                        Object.keys(csvAdBlueData).forEach(key => {
                                          if (key.startsWith(car.id + ':')) {
                                            totalAdBlue += csvAdBlueData[key]?.litri || 0;
                                          }
                                        });
                                        return totalAdBlue > 0 ? `${totalAdBlue.toFixed(1)}L` : '-';
                                      })()}
                                    </p>
                                  </div>
                                  {/* Combustibil Consumat (Formula) */}
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Comb. Consumat</p>
                                    {(() => {
                                      const hasStartLevel = tripSheets[car.id].fuel_level?.start_level != null && tripSheets[car.id].fuel_level?.start_level !== '';
                                      const hasEndLevel = tripSheets[car.id].fuel_level?.end_level != null && tripSheets[car.id].fuel_level?.end_level !== '';
                                      const startLevel = parseFloat(tripSheets[car.id].fuel_level?.start_level) || 0;
                                      const endLevel = parseFloat(tripSheets[car.id].fuel_level?.end_level) || 0;
                                      const alimentari = parseFloat(tripSheets[car.id].summary.total_fuel_liters) || 0;
                                      
                                      // Formula: (Nivel Început + Alimentări) - Nivel Sfârșit
                                      const consumTotal = (startLevel + alimentari) - endLevel;
                                      const formulaText = `(${startLevel.toFixed(2)}L început + ${alimentari.toFixed(2)}L alimentări) - ${endLevel.toFixed(2)}L sfârșit = ${consumTotal.toFixed(2)}L`;
                                      
                                      if (!hasStartLevel && !hasEndLevel) {
                                        return (
                                          <p className="text-sm font-semibold text-slate-500" title="Setați nivelul rezervorului pentru calcul exact">
                                            ~{alimentari.toFixed(1)}L*
                                          </p>
                                        );
                                      } else if (!hasEndLevel) {
                                        return (
                                          <p className="text-sm font-semibold text-amber-600" title="Lună în curs - setați nivelul de sfârșit de lună">
                                            ~{alimentari.toFixed(1)}L**
                                          </p>
                                        );
                                      } else {
                                        return (
                                          <div>
                                            <p className="text-sm font-bold text-orange-600">
                                              {consumTotal > 0 ? `${consumTotal.toFixed(2)}L` : '-'}
                                            </p>
                                            <p className="text-[9px] text-slate-400 leading-tight" title={formulaText}>
                                              ({startLevel.toFixed(0)}+{alimentari.toFixed(1)}-{endLevel.toFixed(0)})
                                            </p>
                                          </div>
                                        );
                                      }
                                    })()}
                                  </div>
                                  {/* Consum Mediu L/100km */}
                                  <div>
                                    <p className="text-slate-600 text-[10px]">Consum Mediu</p>
                                    {(() => {
                                      const hasStartLevel = tripSheets[car.id].fuel_level?.start_level != null && tripSheets[car.id].fuel_level?.start_level !== '';
                                      const hasEndLevel = tripSheets[car.id].fuel_level?.end_level != null && tripSheets[car.id].fuel_level?.end_level !== '';
                                      const startLevel = parseFloat(tripSheets[car.id].fuel_level?.start_level) || 0;
                                      const endLevel = parseFloat(tripSheets[car.id].fuel_level?.end_level) || 0;
                                      const alimentari = parseFloat(tripSheets[car.id].summary.total_fuel_liters) || 0;
                                      const kmParcursi = parseFloat(tripSheets[car.id].summary.total_distance) || 0;
                                      
                                      const consumTotal = (startLevel + alimentari) - endLevel;
                                      const consumMediu = kmParcursi > 0 ? (consumTotal / kmParcursi * 100) : 0;
                                      
                                      if (kmParcursi === 0) {
                                        return <p className="text-sm font-semibold text-slate-400">- (0 km)</p>;
                                      }
                                      
                                      if (!hasStartLevel && !hasEndLevel) {
                                        // Estimare bazată doar pe alimentări
                                        const estimare = (alimentari / kmParcursi * 100);
                                        return (
                                          <p className="text-sm font-semibold text-slate-500" title="Estimare bazată doar pe alimentări">
                                            ~{estimare.toFixed(1)}*
                                          </p>
                                        );
                                      }
                                      
                                      const colorClass = consumMediu > 15 ? 'text-red-600' : consumMediu < 8 ? 'text-green-600' : 'text-blue-600';
                                      return (
                                        <p className={`text-sm font-semibold ${colorClass}`}>
                                          {consumMediu > 0 ? `${consumMediu.toFixed(1)} L/100km` : '-'}
                                        </p>
                                      );
                                    })()}
                                  </div>
                                </div>
                                
                                {/* Explicație formula și avertismente */}
                                {(() => {
                                  const hasStartLevel = tripSheets[car.id].fuel_level?.start_level != null && tripSheets[car.id].fuel_level?.start_level !== '';
                                  const hasEndLevel = tripSheets[car.id].fuel_level?.end_level != null && tripSheets[car.id].fuel_level?.end_level !== '';
                                  
                                  if (!hasStartLevel && !hasEndLevel) {
                                    return (
                                      <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-amber-600 flex items-center gap-1">
                                        <span>⚠️</span>
                                        <span>* Setați nivelul rezervorului (butonul albastru) pentru calcul exact al consumului</span>
                                      </div>
                                    );
                                  } else if (!hasEndLevel) {
                                    return (
                                      <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-amber-600 flex items-center gap-1">
                                        <span>⏳</span>
                                        <span>** Lună în curs - setați nivelul rezervor sfârșit lună pentru consum final</span>
                                      </div>
                                    );
                                  } else {
                                    const startLevel = parseFloat(tripSheets[car.id].fuel_level?.start_level) || 0;
                                    const endLevel = parseFloat(tripSheets[car.id].fuel_level?.end_level) || 0;
                                    const alimentari = parseFloat(tripSheets[car.id].summary.total_fuel_liters) || 0;
                                    const consumTotal = (startLevel + alimentari) - endLevel;
                                    
                                    return (
                                      <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-500">
                                        <span className="font-medium">Formula:</span> ({startLevel}L început + {alimentari.toFixed(2)}L alimentări) - {endLevel}L sfârșit = <span className="text-orange-600 font-semibold">{consumTotal.toFixed(1)}L</span>
                                      </div>
                                    );
                                  }
                                })()}
                              </div>
                            </>
                          ) : (
                            <div className="text-center py-8 text-slate-500">
                              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                              <p>Nu există curse în {new Date(selectedYear, selectedMonth - 1).toLocaleString('ro-RO', { month: 'long' })} {selectedYear}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-slate-500">Se încarcă...</p>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
                );
              })
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
});

export default FoiDeParcursTab;
